/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/cardPlacementRows.ts
 * FILE_ID: UI-KANBAN-DRAWER-PLACEMENT-ROWS-001
 * PURPOSE: Pure row-model for the "Boards & lanes" drawer section — turns the
 *          card's home context + ARC-10 placements into typed rows carrying the
 *          real `Board › Lane` label, a board-state tag, a "You are here" flag
 *          and a navigable/disabled decision for the "Open board" affordance.
 * WHY: The drawer mislabelled a card whose HOME board was a DIFFERENT (often
 *      soft-deleted) board as "This board", and gave no way to see a referenced
 *      board's state or navigate to it. This model is unit-tested so the
 *      degrade-gracefully rules (missing state → active, missing accessible →
 *      true) and the "never navigate to trash/archived/inaccessible/current"
 *      gate can't silently regress.
 * RELATED:
 *   - ./CardPlacementsSection.tsx: renders these rows.
 *   - ../../../types/card.ts: Card + CardLanePlacement contracts.
 */
import type { Card, CardLanePlacement, Lane } from '../../../types'

export type BoardState = 'active' | 'archived' | 'deleted'

export interface PlacementRowVM {
  /** Stable React key. */
  key: string
  /** True for the home presence row (no remove button). */
  isHome: boolean
  /** Placement id — present only on placement rows (drives Remove). */
  placementId?: string
  /** The board this presence lives on (null when unknown / not on wire). */
  boardId: string | null
  /** Display label — "This board › Lane" | "Board › Lane" | "This board". */
  label: string
  /** Lifecycle of the presence's board (missing → 'active'). */
  state: BoardState
  /** Whether the viewer can access the board (missing → true). */
  accessible: boolean
  /** True when this presence is on the board the operator is viewing. */
  isCurrent: boolean
  /** Small pill descriptor, or null for an accessible active board. */
  tag: { label: string; tone: 'archived' | 'deleted' | 'noaccess' } | null
  /** True when "Open board" could navigate IF a handler is wired: an
   *  active, accessible, non-current board with a known id. */
  navigable: boolean
  /** Why "Open board" is disabled (null when navigable). Used as the
   *  tooltip on the disabled affordance. */
  disabledReason: string | null
}

function locationLabel(
  boardName: string | null | undefined,
  laneName: string | null | undefined,
): string | null {
  const board = boardName || null
  const lane = laneName || null
  if (board && lane) return `${board} › ${lane}`
  if (lane) return lane
  if (board) return board
  return null
}

function tagFor(
  state: BoardState,
  accessible: boolean,
): PlacementRowVM['tag'] {
  if (state === 'deleted') return { label: 'Deleted', tone: 'deleted' }
  if (state === 'archived') return { label: 'Archived', tone: 'archived' }
  if (!accessible) return { label: 'No access', tone: 'noaccess' }
  return null
}

function disabledReasonFor(
  state: BoardState,
  accessible: boolean,
  isCurrent: boolean,
  boardId: string | null,
): string | null {
  if (isCurrent) return null
  if (state === 'deleted')
    return 'This board is in the trash — restore it from Recently deleted boards'
  if (state === 'archived') return 'This board is archived'
  if (!accessible) return "You don't have access to this board"
  if (!boardId) return "This board can't be opened"
  return null
}

/**
 * The home presence row. When `card.home_board_id` is absent (pre-field
 * payload) the home is treated as the CURRENT board — exactly the old
 * behaviour — so nothing breaks during a partial deploy.
 */
export function buildHomeRow(
  card: Card,
  boardId: string | undefined,
  lanes: Lane[],
): PlacementRowVM {
  const homeBoardId = card.home_board_id ?? null
  // Missing home_board_id → assume the home presence is on this board.
  const isCurrent = homeBoardId === null ? true : homeBoardId === boardId
  const state: BoardState = card.home_board_state ?? 'active'
  const accessible = card.home_board_accessible ?? true
  const rowBoardId = homeBoardId ?? boardId ?? null

  // Prefer the wire-provided home lane name; fall back to the current
  // board's lane lookup (only meaningful when home IS the current board).
  const homeLaneName =
    card.home_lane_name ?? lanes.find((l) => l.id === card.lane)?.name ?? null

  let label: string
  if (isCurrent) {
    label = homeLaneName ? `This board › ${homeLaneName}` : 'This board'
  } else {
    label = locationLabel(card.home_board_name, homeLaneName) ?? 'Home board'
  }

  return {
    key: 'home',
    isHome: true,
    boardId: rowBoardId,
    label,
    state,
    accessible,
    isCurrent,
    tag: tagFor(state, accessible),
    navigable: state === 'active' && accessible && !isCurrent && !!rowBoardId,
    disabledReason: disabledReasonFor(state, accessible, isCurrent, rowBoardId),
  }
}

/** One placement (extra live presence) row. */
export function buildPlacementRow(
  p: CardLanePlacement,
  boardId: string | undefined,
): PlacementRowVM {
  const rowBoardId = p.board ?? null
  const isCurrent = rowBoardId !== null && rowBoardId === boardId
  const state: BoardState = p.board_state ?? 'active'
  const accessible = p.board_accessible ?? true
  const label = locationLabel(p.board_name, p.lane_name) ?? p.lane

  return {
    key: p.id,
    isHome: false,
    placementId: p.id,
    boardId: rowBoardId,
    label,
    state,
    accessible,
    isCurrent,
    tag: tagFor(state, accessible),
    navigable: state === 'active' && accessible && !isCurrent && !!rowBoardId,
    disabledReason: disabledReasonFor(state, accessible, isCurrent, rowBoardId),
  }
}

/** Home row followed by one row per placement. */
export function buildPlacementRows(
  card: Card,
  boardId: string | undefined,
  lanes: Lane[],
): PlacementRowVM[] {
  const rows: PlacementRowVM[] = [buildHomeRow(card, boardId, lanes)]
  for (const p of card.lane_placements ?? []) {
    rows.push(buildPlacementRow(p, boardId))
  }
  return rows
}
